import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Button } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import { AppBarContext } from '../appBar';
import type { IconName } from '../icons/registry';
import { tabbarQuery, useMediaQuery } from '../hooks/useMediaQuery';
import { useLinkComponent } from '../navigation';
import styles from './AppShell.module.css';

export interface AppShellTab {
  key: string;
  label: string;
  href: string;
  icon: IconName;
  active?: boolean;
}

export interface AppShellProps {
  /** Logo / product name shown in sidebar and top bar. */
  brand: ReactNode;
  /** A shorter brand for the top bar of phones and tablets; defaults to `brand`. */
  topbarBrand?: ReactNode;
  /** Full navigation (typically a `SideNav`). Receives a callback to close the drawer. */
  navigation: (close: () => void) => ReactNode;
  /**
   * The four most used destinations as a tab bar on phones and tablets
   * (docs/ux-richtlinien.md §5). Phones and tablets then have no sidebar; the
   * last tab "Mehr" leads to `more`, a page with the remaining destinations
   * (#80). Without tabs, the top bar shows a menu button for the sidebar.
   */
  tabs?: AppShellTab[];
  /** Target of the last tab "Mehr"; `active` while none of the tabs is. */
  more?: { href: string; active?: boolean };
  /** Compact actions next to the brand, e.g. a `+` quick-capture menu. */
  headerActions?: ReactNode;
  /** Content below the brand in the sidebar. Can receive `close`. */
  sidebarHeader?: ReactNode | ((close: () => void) => ReactNode);
  /** Content at the bottom of the sidebar (e.g. the account menu). Can receive `close`. */
  sidebarFooter?: ReactNode | ((close: () => void) => ReactNode);
  /** Labels are passed in because the design system has no own texts (i18n). */
  labels: {
    skipToContent: string;
    openMenu: string;
    closeMenu: string;
    /** Label of the last tab that leads to the remaining destinations, e.g. "Mehr". */
    more?: string;
    /** Accessible name of the tab bar, e.g. "Bereiche". */
    tabs?: string;
  };
  children: ReactNode;
}

/**
 * Application frame: sidebar on wide screens; top bar and tab bar on phones
 * and tablets (without tabs: a drawer with the full navigation). Skip link
 * and landmarks for assistive technology.
 */
export function AppShell({
  brand,
  topbarBrand,
  navigation,
  tabs,
  more,
  headerActions,
  sidebarHeader,
  sidebarFooter,
  labels,
  children,
}: AppShellProps) {
  const [open, setOpen] = useState(false);
  const drawerId = useId();
  const mainId = useId();
  const Link = useLinkComponent();
  const close = () => setOpen(false);
  const hasTabs = Boolean(tabs && tabs.length > 0);
  // Only rendered where it is visible, so links are not doubled for assistive tech.
  const showTabbar = useMediaQuery(tabbarQuery);
  const [backSlot, setBackSlot] = useState<HTMLElement | null>(null);
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  const slots = useMemo(() => ({ back: backSlot, title: titleSlot }), [backSlot, titleSlot]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={[styles.shell, hasTabs && styles.withTabs].filter(Boolean).join(' ')}>
      <a href={`#${mainId}`} className={styles.skip}>
        {labels.skipToContent}
      </a>

      <header className={styles.topbar}>
        {!hasTabs && (
          <Button
            variant="ghost"
            aria-expanded={open}
            aria-controls={drawerId}
            aria-label={open ? labels.closeMenu : labels.openMenu}
            onClick={() => setOpen((o) => !o)}
            iconStart={<Icon name={open ? 'close' : 'menu'} size="md" />}
          />
        )}
        <div className={styles.barBack} ref={setBackSlot} />
        <div className={styles.brand}>{topbarBrand ?? brand}</div>
        <div className={styles.barTitle} ref={setTitleSlot} />
        {headerActions && <div className={styles.actions}>{headerActions}</div>}
      </header>

      {/* Always rendered: it fades out, too. Hidden on wide screens by the CSS. */}
      <div
        className={[styles.backdrop, open && styles.backdropOpen].filter(Boolean).join(' ')}
        onClick={close}
        aria-hidden="true"
      />

      {/* With a tab bar, phones and tablets have no sidebar at all (#80). */}
      {!(hasTabs && showTabbar) && (
        <aside
          id={drawerId}
          className={[styles.sidebar, open && styles.open].filter(Boolean).join(' ')}
        >
          <div className={styles.sidebarBrand}>
            <div className={styles.brand}>{brand}</div>
            {headerActions && <div className={styles.actions}>{headerActions}</div>}
          </div>
          {sidebarHeader && (
            <div className={styles.sidebarHeader}>
              {typeof sidebarHeader === 'function' ? sidebarHeader(close) : sidebarHeader}
            </div>
          )}
          <div className={styles.sidebarNav}>{navigation(close)}</div>
          {sidebarFooter && (
            <div className={styles.sidebarFooter}>
              {typeof sidebarFooter === 'function' ? sidebarFooter(close) : sidebarFooter}
            </div>
          )}
        </aside>
      )}

      <main id={mainId} className={styles.main} tabIndex={-1}>
        <div className={styles.content}>
          <AppBarContext.Provider value={slots}>{children}</AppBarContext.Provider>
        </div>
      </main>

      {hasTabs && showTabbar && (
        <nav
          className={styles.tabbar}
          aria-label={labels.tabs ?? labels.more ?? labels.openMenu}
          data-tabbar=""
        >
          {tabs?.map((tab) => (
            <Link
              key={tab.key}
              href={tab.href}
              className={[styles.tab, tab.active && styles.tabActive].filter(Boolean).join(' ')}
              {...(tab.active ? { 'aria-current': 'page' as const } : {})}
            >
              <Icon name={tab.icon} size="lg" />
              <span className={styles.tabLabel}>{tab.label}</span>
            </Link>
          ))}
          {more && (
            <Link
              href={more.href}
              className={[styles.tab, more.active && styles.tabActive].filter(Boolean).join(' ')}
              {...(more.active ? { 'aria-current': 'page' as const } : {})}
            >
              <Icon name="more-horizontal" size="lg" />
              <span className={styles.tabLabel}>{labels.more ?? labels.openMenu}</span>
            </Link>
          )}
        </nav>
      )}
    </div>
  );
}
